import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, Boxes, Check, FileCode2, Flame, Instagram, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { DemoQuote } from "@/components/DemoQuote";
import { InstallPrompt } from "@/components/InstallPrompt";
import { SeatsCounter } from "@/components/SeatsCounter";
import { RequestsWall } from "@/components/RequestsWall";
import { SegmentPicker } from "@/components/SegmentPicker";
import { ProductHuntBadge } from "@/components/ProductHuntBadge";
import { money, pick } from "@/lib/pricing";
import { setSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";

const COPY = {
  it: {
    login: "Accedi",
    start: "Prova gratis 7 giorni",
    startShort: "Prova gratis",
    kicker: "Preventivi, fatture e fatturazione elettronica · per professionisti e imprese",
    h1a: "La burocrazia del tuo lavoro,",
    h1b: "finalmente in automatico.",
    heroLead: "Tu fai il tuo lavoro. Operativo AI si occupa del resto.",
    heroP1: "Crea un preventivo in pochi secondi. Trasformalo in fattura con un click. Gestisci l'invio della fattura elettronica e i solleciti senza dover ricordare tutto.",
    heroP2: "E più usi Operativo AI, più impara come lavori.",
    reserveCta: "Prenota il tuo posto",
    reserveShort: "Prenota",
    heroPrice: "Accesso Fondatori: 49,90 € per i primi 6 mesi",
    heroPriceAfter: "Poi 14,90 €/mese.",
    heroNote: "Il pagamento avverrà solo al momento dell'attivazione. Prenotando ora blocchi l'offerta di lancio.",
    heroTrial: "Oppure inizia subito la prova gratuita di 7 giorni",
    heroTryNow: "Provalo qui sotto: scrivi un lavoro tuo, senza registrarti",

    flowTitle: "Da preventivo a pagamento.",
    flowTitle2: "Senza inseguire la burocrazia.",
    painsTitle: "Quante volte hai dovuto:",
    pains: [
      "creare un preventivo;",
      "ricopiare gli stessi dati in una fattura;",
      "preparare e inviare la fattura elettronica;",
      "controllare chi ha pagato;",
      "ricordarti chi devi sollecitare;",
      "rifare gli stessi passaggi per ogni cliente?",
    ],
    flowNote: "Con Operativo AI, queste attività diventano un unico flusso operativo.",
    steps: [
      { t: "Crea il preventivo", d: "Inserisci il cliente e il lavoro da fare. Operativo AI prepara il preventivo." },
      { t: "Un click e passi alla fattura", d: "Quando il preventivo diventa un incarico, i dati sono già lì. Niente copia-incolla." },
      { t: "Lascia che Operativo AI segua il resto", d: "Gestisci la fatturazione elettronica e automatizza i solleciti per non dover inseguire manualmente ogni pagamento." },
    ],

    videoTitle: "Guarda cosa fa, per davvero",
    videoSub: "Due registrazioni reali dell'app, senza montaggio né animazioni.",
    videoNote: "I video partono da soli, senza audio.",
    clips: [
      { file: "demo", t: "Guarda il preventivo mentre si autocompila", d: "Da due righe di testo a un preventivo con voci, quantità, IVA e totale." },
      { file: "reminder", t: "Il sollecito lo scrive lui", d: "Fattura scaduta da mesi: email pronta da inviare, col tono giusto." },
    ],

    sdiBadge: "Solo in Italia",
    sdiTitle: "Fatturazione elettronica inclusa",
    sdiText: "La fattura elettronica è parte del flusso: da ogni fattura ottieni il file XML FatturaPA valido per l'Agenzia delle Entrate, con natura e riferimento normativo corretti anche in regime forfettario. Nessun campo da compilare a mano.",
    sdiCta: "Come funziona",

    learnsTitle: "E non è solo automazione.",
    learnsLead: "Operativo AI impara come lavori.",
    learnsP1: "Più lo usi, più conosce le tue abitudini, i tuoi clienti e il tuo modo di lavorare.",
    learnsP2: "Le informazioni che utilizzi più spesso diventano parte della tua operatività.",
    learnsStrong: "Meno istruzioni. Meno ripetizioni. Più lavoro fatto.",
    learnsP3: "È come avere un assistente amministrativo che diventa sempre più bravo a lavorare con te.",

    timeTitle: "Quanto vale un'ora del tuo tempo?",
    timeP1: "Se sei un professionista, ogni ora passata a fare amministrazione è un'ora che non dedichi ai tuoi clienti, al tuo lavoro o alla crescita della tua attività.",
    timeP2: "Operativo AI nasce per eliminare proprio questo tempo.",
    timeBullets: ["Meno burocrazia.", "Meno attività ripetitive.", "Più tempo per lavorare."],

    shotsTitle: "Com'è fatto dentro",
    shotsSub: "Schermate reali dell'app, con i dati di un'impresa di impianti.",
    shots: [
      { file: "dashboard", t: "Cruscotto", d: "Fatturato, da incassare, preventivi aperti e la memoria del lavoro svolto in un colpo d'occhio." },
      { file: "preventivi", t: "Preventivi e fatture", d: "Numerazione automatica, stato, PDF da scaricare e invio email con un clic." },
      { file: "report", t: "Report incassi", d: "Fatturato e incassato mese per mese, con export CSV per il commercialista." },
      { file: "agenda", t: "Agenda", d: "Le attività del giorno con priorità e l'AI che ti mette in ordine la giornata." },
    ],

    priceTitle: "Accesso Fondatori",
    founderPer: "per 6 mesi",
    founderWho: "Per chi entra durante il lancio.",
    founderAfter: "Dopo i primi 6 mesi: 14,90 €/mese, prezzo bloccato per sempre.",
    founderIncludes: "L'accesso comprende le funzionalità disponibili di Operativo AI e gli aggiornamenti rilasciati durante il periodo.",
    founderBook: "Prenota ora il tuo posto.",
    founderNoPay: "Non devi pagare adesso.",
    founderLeave: "Lascia il tuo contatto e sarai tra i primi a ricevere l'accesso quando Operativo AI sarà ufficialmente disponibile.",
    reserveButton: "Prenota il mio posto",
    seatsNote: "Posti riservati alla fase di lancio.",
    reserveDone: "Posto prenotato. Controlla la casella: la tua offerta di lancio è bloccata.",
    reserveSeat: "Il tuo numero di posto:",
    reserveError: "Inserisci un indirizzo email valido.",
    reserveLocked: [
      "49,90 € per i primi 6 mesi, garantito — poi 14,90 €/mese bloccati per sempre",
      "+20 azioni AI ogni mese, per sempre",
      "Non paghi adesso: ti avvisiamo per primo all'attivazione.",
    ],
    shareTitle: "Conosci qualcuno che perde tempo con la burocrazia?",
    shareWhy: "I posti sono riservati alla fase di lancio — mandagli il link.",
    shareText: "Ho prenotato il mio posto su Operativo AI: preventivi, fatture e fattura elettronica in un unico flusso. Offerta di lancio bloccata:",
    copyLink: "Copia il link",
    copied: "Link copiato",
    emailPlaceholder: "tu@azienda.it",

    proName: "Abbonamento mensile",
    per: "/ mese",
    atLaunch: "all'attivazione",
    orYear: "Oppure",
    perYear: "all'anno",
    yearSave: "risparmi",
    trialTitle: "Vuoi provarlo adesso?",
    trialText: "La prova è gratuita per 7 giorni, senza carta. Poi continui con il piano gratuito o passi al mensile.",
    included: "Cosa include",
    benefits: [
      "Preventivi e fatture illimitati",
      "PDF con logo e IBAN",
      "Fattura elettronica in formato FatturaPA",
      "Solleciti automatici e invio email",
      "Report incassi ed export CSV",
      "Interfaccia in italiano e inglese",
    ],
    waitlistTitle: "Non sei ancora pronto? Ti avvisiamo prima che i posti finiscano.",
    waitlistCta: "Avvisami",
    waitlistDone: "Sei in lista. Le tue +20 azioni AI al mese sono riservate.",
    waitlistError: "Inserisci un indirizzo email valido.",
    guarantee: "Garanzia 30 giorni soddisfatti o rimborsati.",
    fairUse: "Fair use: 300 azioni AI al mese (documenti, PDF ed email restano illimitati).",
    freeNote: "Piano gratuito: 3 documenti al mese, per sempre.",
    founderCta: "Prendi l'Accesso Fondatori",
    onlyLeft: "Stanno finendo — solo",
    pillPre: "posti su",
    pillTail: "disponibili · 49,90 € per 6 mesi",
    pillLocked: "Accesso Fondatori: 49,90 € per 6 mesi · fase di lancio",

    whoTitle: "Per chi è Operativo AI?",
    who: [
      { t: "Consulenti", d: "Più tempo per i clienti, meno tempo per l'amministrazione." },
      { t: "Freelance", d: "Fai il tuo lavoro senza trasformarti anche nel tuo amministrativo." },
      { t: "Professionisti", d: "Gestisci preventivi, fatture e solleciti in un unico flusso." },
      { t: "Studi e attività professionali", d: "Riduci le attività ripetitive e mantieni sotto controllo il lavoro amministrativo." },
    ],

    faqTitle: "Domande frequenti",
    faq: [
      { q: "Devo pagare subito?", a: "No. In questa fase stai prenotando il tuo accesso. Il pagamento verrà richiesto quando Operativo AI sarà pronto per l'attivazione." },
      { q: "Quanto costa?", a: "L'offerta Fondatori è di 49,90 € per i primi 6 mesi. Successivamente il piano passa a 14,90 €/mese, con documenti illimitati e nessun costo nascosto: il prezzo resta bloccato per chi entra durante il lancio." },
      { q: "Operativo AI funziona anche in inglese?", a: "Sì. Operativo AI è disponibile in italiano e inglese." },
      { q: "Operativo AI sostituisce il mio commercialista?", a: "No. Operativo AI automatizza le attività operative e amministrative che svolgi nella gestione quotidiana. Non sostituisce il professionista che ti segue per gli aspetti fiscali e contabili." },
      { q: "Cosa succede dopo la prenotazione?", a: "Ti contatteremo quando sarà possibile attivare il servizio e potrai procedere con l'acquisto dell'offerta che hai bloccato." },
    ],

    ctaTitle: "Il tuo lavoro è fare il professionista.",
    ctaLead: "La burocrazia può diventare operativa.",
    ctaButton: "Prenota il tuo posto a 49,90 €",
    tagline: "Operativo AI · Più lo usi. Più ti somiglia.",
    thanks: "Grazie, la tua risposta è arrivata. Mi aiuta davvero.",
    toolLink: "Generatore di preventivi gratis",
    madeIn: "software italiano per professionisti e piccole imprese, sviluppato in Italia",
    legalPrivacy: "Privacy",
    legalTerms: "Termini",
    legalRefund: "Rimborsi",
    instagram: "Seguici su Instagram",
  },
  en: {
    login: "Log in",
    start: "Try free for 7 days",
    startShort: "Try free",
    kicker: "Quotes, invoices and e-invoicing · for professionals and small firms",
    h1a: "The paperwork of your job,",
    h1b: "finally on autopilot.",
    heroLead: "You do your work. Operativo AI takes care of the rest.",
    heroP1: "Create a quote in seconds. Turn it into an invoice with one click. Handle e-invoicing and payment reminders without having to remember everything.",
    heroP2: "And the more you use Operativo AI, the more it learns how you work.",
    reserveCta: "Book your seat",
    reserveShort: "Book",
    heroPrice: "Founders Access: $49.90 for the first 6 months",
    heroPriceAfter: "Then $14.90/month.",
    heroNote: "You'll only be charged when the service goes live. Booking now locks in the launch offer.",
    heroTrial: "Or start the free 7-day trial right away",
    heroTryNow: "Try it right below: type one of your own jobs, no signup",

    flowTitle: "From quote to payment.",
    flowTitle2: "Without chasing paperwork.",
    painsTitle: "How many times have you had to:",
    pains: [
      "write a quote;",
      "retype the same data into an invoice;",
      "prepare and send the e-invoice;",
      "check who has paid;",
      "remember who needs a reminder;",
      "repeat the same steps for every client?",
    ],
    flowNote: "With Operativo AI, all of this becomes one single flow.",
    steps: [
      { t: "Create the quote", d: "Add the client and the job. Operativo AI prepares the quote." },
      { t: "One click to the invoice", d: "When the quote becomes a job, the data is already there. No copy-paste." },
      { t: "Let Operativo AI handle the rest", d: "Manage e-invoicing and automate reminders, so you never chase a payment by hand." },
    ],

    videoTitle: "See what it actually does",
    videoSub: "Two real screen recordings of the app. No editing, no animations.",
    videoNote: "Videos play on their own, without sound.",
    clips: [
      { file: "demo", t: "Watch the quote fill itself in", d: "From two lines of text to a quote with line items, quantities, tax and total." },
      { file: "reminder", t: "It writes the reminder for you", d: "Invoice months overdue: email ready to send, with the right tone." },
    ],

    sdiBadge: "Italy only",
    sdiTitle: "E-invoicing included",
    sdiText: "Italian e-invoicing is part of the flow: every invoice gives you the FatturaPA XML file valid for the Italian Revenue Agency, with the correct nature and legal reference even under the flat-rate regime. No fields to fill in by hand.",
    sdiCta: "How it works",

    learnsTitle: "And it's not just automation.",
    learnsLead: "Operativo AI learns how you work.",
    learnsP1: "The more you use it, the more it knows your habits, your clients and your way of working.",
    learnsP2: "The information you use most often becomes part of your daily flow.",
    learnsStrong: "Fewer instructions. Less repetition. More work done.",
    learnsP3: "It's like having an admin assistant who keeps getting better at working with you.",

    timeTitle: "What is an hour of your time worth?",
    timeP1: "If you're a professional, every hour spent on admin is an hour you don't spend on your clients, your craft or growing your business.",
    timeP2: "Operativo AI exists to remove exactly that time.",
    timeBullets: ["Less paperwork.", "Less repetitive work.", "More time to work."],

    shotsTitle: "What it looks like inside",
    shotsSub: "Real screens of the app, with the data of a plumbing and electrical firm.",
    shots: [
      { file: "dashboard", t: "Dashboard", d: "Revenue, outstanding, open quotes and the memory of the work you did, at a glance." },
      { file: "preventivi", t: "Quotes and invoices", d: "Automatic numbering, status, PDF download and email sending in one click." },
      { file: "report", t: "Revenue report", d: "Invoiced and collected month by month, with CSV export for your accountant." },
      { file: "agenda", t: "Agenda", d: "Today's tasks by priority, with the AI putting your day in order." },
    ],

    priceTitle: "Founders Access",
    founderPer: "for 6 months",
    founderWho: "For everyone joining during the launch.",
    founderAfter: "After the first 6 months: $14.90/month, locked forever.",
    founderIncludes: "Access includes the available features of Operativo AI and every update released during the period.",
    founderBook: "Book your seat now.",
    founderNoPay: "You don't pay anything today.",
    founderLeave: "Leave your contact and you'll be among the first to get access when Operativo AI officially goes live.",
    reserveButton: "Book my seat",
    seatsNote: "Seats reserved for the launch phase.",
    reserveDone: "Seat booked. Check your inbox: your launch offer is locked in.",
    reserveSeat: "Your seat number:",
    reserveError: "Enter a valid email address.",
    reserveLocked: [
      "$49.90 for the first 6 months, guaranteed — then $14.90/month locked forever",
      "+20 AI actions every month, forever",
      "No payment today: you'll be the first to know when we go live.",
    ],
    shareTitle: "Know someone losing hours to paperwork?",
    shareWhy: "Seats are reserved for the launch phase — send them the link.",
    shareText: "I booked my seat on Operativo AI: quotes, invoices and e-invoicing in one single flow. Launch offer locked in:",
    copyLink: "Copy link",
    copied: "Link copied",
    emailPlaceholder: "you@company.com",

    proName: "Monthly plan",
    per: "/ month",
    atLaunch: "at launch",
    orYear: "Or",
    perYear: "per year",
    yearSave: "you save",
    trialTitle: "Want to try it right now?",
    trialText: "The trial is free for 7 days, no card. Then you continue on the free plan or move to monthly.",
    included: "What's included",
    benefits: [
      "Unlimited quotes and invoices",
      "PDF with your logo and bank details",
      "Italian e-invoicing (FatturaPA XML)",
      "Automatic reminders and email sending",
      "Revenue reports and CSV export",
      "Interface in Italian and English",
    ],
    waitlistTitle: "Not ready yet? We'll warn you before the seats run out.",
    waitlistCta: "Notify me",
    waitlistDone: "You're on the list. Your +20 AI actions a month are reserved.",
    waitlistError: "Enter a valid email address.",
    guarantee: "30-day money-back guarantee.",
    fairUse: "Fair use: 300 AI actions per month (documents, PDFs and emails stay unlimited).",
    freeNote: "Free plan: 3 documents per month, forever.",
    founderCta: "Get Founders Access",
    onlyLeft: "Almost gone — only",
    pillPre: "of",
    pillTail: "seats left · $49.90 for 6 months",
    pillLocked: "Founders Access: $49.90 for 6 months · launch phase",

    whoTitle: "Who is Operativo AI for?",
    who: [
      { t: "Consultants", d: "More time for clients, less time on admin." },
      { t: "Freelancers", d: "Do your work without becoming your own back office." },
      { t: "Professionals", d: "Quotes, invoices and reminders in one single flow." },
      { t: "Practices and small firms", d: "Cut repetitive work and keep admin under control." },
    ],

    faqTitle: "Frequently asked questions",
    faq: [
      { q: "Do I have to pay now?", a: "No. At this stage you're booking your access. Payment will be requested when Operativo AI is ready to go live." },
      { q: "How much does it cost?", a: "Founders Access is $49.90 for the first 6 months. After that the plan is $14.90/month, with unlimited documents and no hidden costs. The price stays locked for whoever joins during the launch." },
      { q: "Does Operativo AI work in English?", a: "Yes. Operativo AI is available in Italian and English." },
      { q: "Does Operativo AI replace my accountant?", a: "No. Operativo AI automates the operational and administrative work you do every day. It doesn't replace the professional who handles your tax and accounting." },
      { q: "What happens after I book?", a: "We'll contact you when the service can be activated, and you'll be able to purchase the offer you locked in." },
    ],

    ctaTitle: "Your job is being the professional.",
    ctaLead: "The paperwork can run itself.",
    ctaButton: "Book your seat at $49.90",
    tagline: "Operativo AI · The more you use it, the more it sounds like you.",
    thanks: "Thanks, your answer landed. It really helps.",
    toolLink: "Free quote generator",
    madeIn: "Italian software for professionals and small firms, built in Italy",
    legalPrivacy: "Privacy",
    legalTerms: "Terms",
    legalRefund: "Refunds",
    instagram: "Follow us on Instagram",
  },
};

// Landing pubblica bilingue: la lingua segue il browser (it/en) ed è cambiabile dall'header.
const Landing = () => {
  const { lang, setLang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [shot, setShot] = useState(0);
  const [founder, setFounder] = useState(null);
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [seatNo, setSeatNo] = useState(null);
  const [formError, setFormError] = useState("");
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const activeShot = c.shots[shot];
  const prebook = founder?.mode === "prebook";

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const { data } = await api.post(prebook ? "/public/reserve" : "/public/waitlist", {
        email,
        source: prebook ? "prebook" : "landing",
      });
      setJoined(true);
      setSeatNo(data.seat_no ?? null);
      setFormError("");
      if (typeof data.seats_left === "number") {
        setFounder((f) =>
          f ? { ...f, seats_left: data.seats_left, sold_out: data.seats_left === 0 } : f,
        );
      }
      setEmail("");
      toast.success(prebook ? c.reserveDone : c.waitlistDone);
    } catch {
      setFormError(c.reserveError);
      toast.error(c.waitlistError);
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    // Ripristina titolo e descrizione dopo una visita alle pagine strumento (app a pagina unica).
    const it = lang === "it";
    setSeo({
      title: it
        ? "Operativo AI — preventivi, fatture e fattura elettronica in automatico"
        : "Operativo AI — quotes, invoices and e-invoicing on autopilot",
      description: it
        ? "Software italiano di gestione operativa con AI per professionisti e piccole imprese: crei un preventivo in pochi secondi, lo trasformi in fattura con un click e generi la fattura elettronica XML per l'Agenzia delle Entrate."
        : "Italian software for operational management with AI: create a quote in seconds, turn it into an invoice with one click and generate the FatturaPA e-invoice for the Italian Revenue Agency.",
      path: "/",
      lang: it ? "it" : "en",
      alternates: { it: "/" },
    });
  }, [lang]);

  useEffect(() => {
    // Ritorno dal sondaggio via email: un clic sul motivo → ringraziamento.
    const params = new URLSearchParams(window.location.search);
    if (params.get("grazie") === "1") {
      toast.success(c.thanks);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // ?notrack=1 esclude per sempre questo browser dalle statistiche (visite dell'autrice).
    const params = new URLSearchParams(window.location.search);
    if (params.get("notrack") === "1") localStorage.setItem("op_notrack", "1");
    if (localStorage.getItem("op_notrack") === "1") return;
    if (!sessionStorage.getItem("op_visit_sent")) {
      let visitorId = localStorage.getItem("op_visitor");
      if (!visitorId) {
        visitorId = `v${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        localStorage.setItem("op_visitor", visitorId);
      }
      sessionStorage.setItem("op_visit_sent", "1");
      const params = new URLSearchParams(window.location.search);
      api
        .post("/public/visit", {
          visitor_id: visitorId,
          path: window.location.pathname,
          referrer: document.referrer || "",
          utm: params.get("utm_source") || params.get("ref") || "",
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    const load = () => {
      if (document.hidden) return;
      api.get("/public/founder").then(({ data }) => setFounder(data)).catch(() => {});
    };
    load();
    const timer = setInterval(load, 20000);
    document.addEventListener("visibilitychange", load);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", load);
    };
  }, []);

  const passPrice = pick(lang, founder, "price_usd", "price_eur", 49.9, 49.9);
  const proPrice = pick(lang, founder, "pro_price_usd", "pro_price_eur", 14.9, 14.9);
  const yearPrice = pick(lang, founder, "pro_year_price_usd", "pro_year_price_eur", 149, 149);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <InstallPrompt />
      <header className="glass-header sticky top-0 z-30 flex items-center justify-between px-4 py-4 sm:px-6 lg:px-16">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900">
            <Boxes className="h-4 w-4 text-white" />
          </span>
          <span className="whitespace-nowrap text-base font-extrabold tracking-tight sm:text-lg">Operativo AI</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="flex items-center rounded-full border border-slate-200 p-0.5"
            data-testid="lang-switch"
          >
            {["it", "en"].map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`lang-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase transition-colors ${
                  lang === code
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
          <Link to="/login" data-testid="landing-login">
            <Button variant="ghost" className="px-2 text-sm font-semibold sm:px-4">{c.login}</Button>
          </Link>
          <a href="#prenota" data-testid="landing-cta-top">
            <Button className="gap-2 whitespace-nowrap bg-slate-900 px-3 text-sm text-white hover:bg-slate-800 sm:px-4">
              <span className="hidden sm:inline">{c.reserveCta}</span>
              <span className="sm:hidden">{c.reserveShort}</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </a>
        </div>
      </header>

      <section className="relative overflow-hidden bg-slate-950 px-6 pb-12 pt-14 text-white lg:px-16 lg:pb-16 lg:pt-20 grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(79,70,229,0.4),transparent_55%)]" />
        <div className="relative max-w-3xl">
          <p className="rise text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300">
            {c.kicker}
          </p>
          <h1
            className="rise mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "80ms" }}
            data-testid="hero-title"
          >
            {c.h1a}{" "}
            <span className="bg-gradient-to-r from-indigo-300 to-emerald-300 bg-clip-text text-transparent">
              {c.h1b}
            </span>
          </h1>
          <p
            className="rise mt-5 max-w-2xl text-base font-semibold text-white lg:text-lg"
            style={{ animationDelay: "140ms" }}
          >
            {c.heroLead}
          </p>
          <p
            className="rise mt-4 flex items-center gap-2 text-sm text-emerald-300"
            style={{ animationDelay: "200ms" }}
            data-testid="hero-try-hint"
          >
            <ArrowDown className="h-4 w-4" /> {c.heroTryNow}
          </p>
        </div>
      </section>

      <SegmentPicker />
      <DemoQuote />

      <section
        className="border-y border-slate-200 bg-slate-50 px-6 py-16 lg:px-16"
        id="prenota-pitch"
        data-testid="landing-pitch"
      >
        <div className="max-w-3xl">
          <p className="text-base font-semibold text-slate-900 lg:text-lg">{c.heroP1}</p>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{c.heroP2}</p>
          <div className="mt-8">
            <a href="#prenota" data-testid="landing-cta-hero">
              <Button className="gap-2 bg-indigo-600 px-7 py-6 text-base text-white hover:bg-indigo-500">
                {c.reserveCta} <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
            <p className="mt-6 text-base font-semibold text-slate-900" data-testid="hero-price">
              {c.heroPrice}
            </p>
            <p className="text-sm text-slate-600">{c.heroPriceAfter}</p>
            <p className="mt-4 max-w-lg text-xs italic leading-relaxed text-slate-500">
              {c.heroNote}
            </p>
            <Link
              to="/registrati"
              data-testid="landing-cta-hero-trial"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 underline-offset-4 transition-colors hover:text-indigo-500 hover:underline"
            >
              {c.heroTrial} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {founder && !founder.sold_out && (
            <a
              href="#prenota"
              data-testid="hero-seats-pill"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:border-emerald-600"
            >
              <Flame className="h-3.5 w-3.5" />
              {founder.seats_total - founder.seats_left >= 5 ? (
                <>
                  <span className="font-mono-num">{founder.seats_left}</span>
                  {" "}
                  {c.pillPre} {founder.seats_total} {c.pillTail}
                </>
              ) : (
                c.pillLocked
              )}
            </a>
          )}
          <ProductHuntBadge theme="light" className="mt-8" />
        </div>
      </section>

      <section className="px-6 py-20 lg:px-16" data-testid="landing-flow">
        <div className="mx-auto max-w-5xl">
          <h2 className="max-w-2xl text-3xl font-extrabold leading-tight tracking-tight lg:text-4xl">
            {c.flowTitle}
            <br />
            <span className="text-slate-400">{c.flowTitle2}</span>
          </h2>
          <div className="mt-12 grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <div>
              <p className="text-base font-semibold md:text-lg">{c.painsTitle}</p>
              <ul className="mt-5 space-y-3" data-testid="pains-list">
                {c.pains.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-slate-600">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
                    {p}
                  </li>
                ))}
              </ul>
              <p className="mt-7 max-w-md text-sm font-semibold text-slate-900 md:text-base">
                {c.flowNote}
              </p>
            </div>
            <ol className="space-y-9">
              {c.steps.map((s, i) => (
                <li key={s.t} className="rise flex gap-5" style={{ animationDelay: `${i * 80}ms` }}>
                  <span className="font-mono-num text-2xl font-semibold text-indigo-600">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="text-base font-semibold md:text-lg">{s.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-6 py-20 lg:px-16" data-testid="landing-video">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold tracking-tight text-white lg:text-3xl">
            {c.videoTitle}
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-slate-400 md:text-base">{c.videoSub}</p>
          <div className="mt-8 grid gap-14">
            {c.clips.map((clip) => (
              <figure key={clip.file}>
                <p className="text-base font-semibold text-white md:text-lg">{clip.t}</p>
                <p className="mt-1 mb-4 text-sm text-slate-400">{clip.d}</p>
                <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 p-2 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.9)]">
                  <video
                    key={`${clip.file}-${lang}`}
                    data-testid={`video-${clip.file}`}
                    className="w-full rounded-lg"
                    poster={`${API}/media/${clip.file}-${lang}.jpg`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    controls
                  >
                    <source src={`${API}/media/${clip.file}-${lang}.mp4`} type="video/mp4" />
                    <source src={`${API}/media/${clip.file}-${lang}.webm`} type="video/webm" />
                  </video>
                </div>
              </figure>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">{c.videoNote}</p>
        </div>
      </section>

      {lang === "it" && (
        <section
          className="border-b border-slate-800 bg-slate-900 px-6 py-14 lg:px-16"
          data-testid="sdi-banner"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-300">
                <FileCode2 className="h-3 w-3" /> {c.sdiBadge}
              </span>
              <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white lg:text-3xl">
                {c.sdiTitle}
              </h2>
              <p className="mt-3 text-sm text-slate-400 md:text-base">{c.sdiText}</p>
            </div>
            <Link to="/fattura-elettronica-gratis" data-testid="sdi-banner-cta">
              <Button className="gap-2 bg-white px-6 py-6 text-base text-slate-900 hover:bg-slate-200">
                {c.sdiCta} <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}


      <section className="border-y border-slate-200 bg-slate-50 px-6 py-20 lg:px-16" data-testid="landing-learns">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.learnsTitle}</h2>
          <p className="mt-4 text-base font-semibold md:text-lg">{c.learnsLead}</p>
          <p className="mt-6 text-sm leading-relaxed text-slate-600 md:text-base">{c.learnsP1}</p>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{c.learnsP2}</p>
          <p className="mt-8 text-lg font-extrabold tracking-tight text-slate-900">
            {c.learnsStrong}
          </p>
          <p className="mt-6 text-sm leading-relaxed text-slate-600 md:text-base">{c.learnsP3}</p>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-16" data-testid="landing-time">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.timeTitle}</h2>
          <p className="mt-6 text-sm leading-relaxed text-slate-600 md:text-base">{c.timeP1}</p>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{c.timeP2}</p>
          <div className="mt-9 grid gap-4 sm:grid-cols-3">
            {c.timeBullets.map((b, i) => (
              <p
                key={b}
                className="rise card-flat px-5 py-6 text-base font-semibold md:text-lg"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {b}
              </p>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 px-6 py-20 lg:px-16" data-testid="landing-screenshots">
        <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">{c.shotsTitle}</h2>
        <p className="mt-3 max-w-xl text-sm text-slate-600 md:text-base">{c.shotsSub}</p>
        <div className="mt-8 flex flex-wrap gap-2">
          {c.shots.map((s, i) => (
            <button
              key={s.file}
              type="button"
              onClick={() => setShot(i)}
              data-testid={`shot-tab-${s.file}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                i === shot
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-900"
              }`}
            >
              {s.t}
            </button>
          ))}
        </div>
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 p-2 shadow-[0_24px_60px_-30px_rgba(2,6,23,0.45)]">
          <img
            key={activeShot.file}
            src={`/shots/${activeShot.file}.webp`}
            alt={activeShot.t}
            loading="lazy"
            width="1600"
            height="1000"
            data-testid="shot-image"
            className="rise w-full rounded-lg border border-slate-200 bg-white"
          />
        </div>
        <p className="mt-4 max-w-2xl text-sm text-slate-600">{activeShot.d}</p>
      </section>

      <section className="px-6 py-20 lg:px-16" id="prenota">
        <span id="prezzo" />
        <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.priceTitle}</h2>
        <div className="mt-10 grid gap-6 lg:grid-cols-2 lg:items-start">
          <div
            className="card-flat relative overflow-hidden border-indigo-500/30 bg-slate-950 p-8 text-slate-100 grain"
            data-testid="founder-card"
          >
            <div className="relative">
              <p className="font-mono-num text-5xl font-semibold">
                {money(lang, passPrice)}{" "}
                <span className="text-base font-normal text-slate-400">{c.founderPer}</span>
              </p>
              <p className="mt-4 text-sm text-slate-300">{c.founderWho}</p>
              <p className="mt-2 text-base font-semibold text-white" data-testid="pass-after">
                {c.founderAfter}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{c.founderIncludes}</p>
              {founder && <SeatsCounter offer={founder} lang={lang} />}

              {prebook ? (
                <div className="mt-6" data-testid="prebook-block">
                  {joined && seatNo ? (
                    <div data-testid="reserve-success">
                      <p className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
                        <Check className="h-4 w-4" /> {c.reserveDone}
                      </p>
                      <p className="font-mono-num mt-4 text-4xl font-semibold" data-testid="seat-number">
                        {c.reserveSeat} #{seatNo}
                      </p>
                      <ul className="mt-5 space-y-2 text-sm text-slate-300">
                        {c.reserveLocked.map((line) => (
                          <li key={line} className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                            {line}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 text-sm font-semibold text-slate-200">{c.shareTitle}</p>
                      <p className="mt-1 text-xs text-slate-400">{c.shareWhy}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a
                          data-testid="share-whatsapp"
                          href={`https://wa.me/?text=${encodeURIComponent(`${c.shareText} ${shareUrl}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-emerald-500 hover:text-emerald-300"
                        >
                          WhatsApp
                        </a>
                        <a
                          data-testid="share-linkedin"
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-indigo-500 hover:text-indigo-300"
                        >
                          LinkedIn
                        </a>
                        <button
                          data-testid="share-copy"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(shareUrl);
                              setCopied(true);
                              toast.success(c.copied);
                            } catch {
                              toast.info(shareUrl);
                            }
                          }}
                          className="rounded-full border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
                        >
                          {copied ? c.copied : c.copyLink}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-base font-semibold text-white">{c.founderBook}</p>
                      <p className="mt-1 text-sm text-slate-300">{c.founderNoPay}</p>
                      <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.founderLeave}</p>
                      <form onSubmit={joinWaitlist} className="mt-5 flex flex-col gap-2 sm:flex-row">
                        <input
                          data-testid="reserve-email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={c.emailPlaceholder}
                          className="h-12 flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
                        />
                        <Button
                          data-testid="reserve-submit"
                          type="submit"
                          disabled={joining || founder?.sold_out}
                          className="h-12 gap-2 bg-indigo-600 px-6 text-base text-white hover:bg-indigo-500"
                        >
                          {joining ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Flame className="h-4 w-4" />
                          )}
                          {c.reserveButton}
                        </Button>
                      </form>
                      {formError && (
                        <p className="mt-3 text-sm font-semibold text-red-400" data-testid="reserve-error">
                          {formError}
                        </p>
                      )}
                      <p className="mt-3 text-xs italic text-slate-500">{c.seatsNote}</p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/registrati" data-testid="landing-cta-pass">
                    <Button className="mt-6 w-full gap-2 bg-indigo-600 py-6 text-base text-white hover:bg-indigo-500">
                      {c.founderCta} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <p className="mt-4 text-xs text-slate-400">{c.guarantee}</p>
                  <p className="mt-1.5 text-xs text-slate-500">{c.fairUse}</p>

                  <div className="mt-7 border-t border-slate-800 pt-6">
                    <p className="text-sm text-slate-300">{c.waitlistTitle}</p>
                    <form onSubmit={joinWaitlist} className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <input
                        data-testid="waitlist-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={c.emailPlaceholder}
                        className="h-11 flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
                      />
                      <Button
                        data-testid="waitlist-submit"
                        type="submit"
                        disabled={joining}
                        variant="outline"
                        className="h-11 gap-2 border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800 hover:text-white"
                      >
                        {joining ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        {c.waitlistCta}
                      </Button>
                    </form>
                    {joined && (
                      <p
                        className="mt-2 text-xs font-semibold text-emerald-400"
                        data-testid="waitlist-done"
                      >
                        {c.waitlistDone}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card-flat border-slate-200 p-8" data-testid="pro-card">
            <p className="text-sm font-semibold text-slate-500">{c.proName}</p>
            <p className="font-mono-num mt-3 text-5xl font-semibold">
              {money(lang, proPrice)}{" "}
              <span className="text-base font-normal text-slate-500">
                {c.per}
                {prebook ? ` · ${c.atLaunch}` : ""}
              </span>
            </p>
            <div
              className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg bg-slate-100 px-3 py-2.5"
              data-testid="yearly-price"
            >
              <span className="text-sm font-semibold text-slate-900">
                {c.orYear} <span className="font-mono-num">{money(lang, yearPrice)}</span>{" "}
                {c.perYear}
              </span>
              <span className="text-xs font-semibold text-emerald-700">
                {c.yearSave}{" "}
                <span className="font-mono-num">{money(lang, proPrice * 12 - yearPrice)}</span>{" "}
                ({Math.round((1 - yearPrice / (proPrice * 12)) * 100)}%)
              </span>
            </div>
            <p className="mt-6 text-base font-semibold md:text-lg">{c.trialTitle}</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.trialText}</p>
            <Link to="/registrati" data-testid="landing-cta-price">
              <Button
                variant="outline"
                className="mt-6 w-full gap-2 border-slate-900 py-6 text-base font-semibold hover:bg-slate-900 hover:text-white"
              >
                {c.start} <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <p className="mt-4 text-xs text-slate-500">{c.freeNote}</p>
            <p className="mt-8 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {c.included}
            </p>
            <ul className="mt-4 grid gap-3">
              {c.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50 px-6 py-20 lg:px-16" data-testid="landing-who">
        <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.whoTitle}</h2>
        <div className="mt-10 grid gap-x-12 gap-y-9 md:grid-cols-2">
          {c.who.map((w, i) => (
            <div key={w.t} className="rise" style={{ animationDelay: `${i * 70}ms` }}>
              <h3 className="text-base font-semibold md:text-lg">{w.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      <RequestsWall />

      <section className="px-6 py-20 lg:px-16" data-testid="landing-faq">
        <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.faqTitle}</h2>
        <div className="mt-8 max-w-2xl divide-y divide-slate-200">
          {c.faq.map((f) => (
            <div key={f.q} className="py-5">
              <h3 className="text-base font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 px-6 py-24 text-white lg:px-16 grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(16,185,129,0.25),transparent_55%)]" />
        <div className="relative max-w-2xl">
          <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">{c.ctaTitle}</h2>
          <p className="mt-4 text-2xl font-extrabold tracking-tight text-slate-300 lg:text-3xl">
            {c.ctaLead}
          </p>
          <a href="#prenota" data-testid="landing-cta-bottom">
            <Button className="mt-9 gap-2 bg-white px-7 py-6 text-base text-slate-900 hover:bg-slate-200">
              {c.ctaButton} <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
          <p className="mt-10 text-sm font-semibold text-slate-400" data-testid="landing-tagline">
            {c.tagline}
          </p>
        </div>
      </section>

      <footer className="flex flex-wrap items-center justify-between gap-3 px-6 py-8 pb-40 text-xs text-slate-500 sm:pb-8 lg:px-16">
        <span data-testid="footer-identity">
          © {new Date().getFullYear()} Operativo AI · operativo-ai.com · {c.madeIn}
        </span>
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to={lang === "it" ? "/generatore-preventivi-gratis" : "/free-quote-generator"}
            className="hover:text-slate-900"
            data-testid="footer-tool"
          >
            {c.toolLink}
          </Link>
          {lang === "it" && (
            <Link
              to="/fattura-elettronica-gratis"
              className="hover:text-slate-900"
              data-testid="footer-sdi"
            >
              Fattura elettronica
            </Link>
          )}
          <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy">
            {c.legalPrivacy}
          </Link>
          <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms">
            {c.legalTerms}
          </Link>
          <Link to="/refund" className="hover:text-slate-900" data-testid="footer-refund">
            {c.legalRefund}
          </Link>
          <Link to="/login" className="font-semibold hover:text-slate-900">{c.login}</Link>
        </div>
        <a
          href="https://www.instagram.com/operativoai"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-instagram"
          className="flex w-full items-center gap-2 text-slate-500 transition-colors hover:text-slate-900"
        >
          <Instagram className="h-4 w-4" />
          {c.instagram}
        </a>
      </footer>
    </div>
  );
};

export default Landing;
